const ASSET_PATTERN = /\/js\/app\.([a-f\d]+)\.js(?:[?#]|$)/i
const SCRIPT_PATTERN = /<script\b[^>]*\bsrc=["']([^"']*\/js\/app\.[a-f\d]+\.js(?:[?#][^"']*)?)["'][^>]*>/i
const RELOAD_PARAM = '__mq_reload'
const RELOAD_KEY = 'microquake:resource-reload'

export function isChunkLoadError(error) {
  return /ChunkLoadError|Loading (?:CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(`${error?.name || ''} ${error?.message || ''}`)
}

// 只接受当前站点带内容哈希的入口资源，避免将错误页、登录跳转或代理页面当成新版本。
export function parseRelease(html, origin) {
  const match = typeof html === 'string' && html.match(SCRIPT_PATTERN)
  if (!match) return null
  const asset = new URL(match[1], origin)
  const version = asset.pathname.match(ASSET_PATTERN)?.[1]
  return asset.origin === origin && version ? { version, asset: asset.href } : null
}

export function createAppUpdate({ windowRef = window, documentRef = document, fetchImpl = fetch, now = Date.now, notify = () => {} } = {}) {
  const origin = windowRef.location.origin
  const version = Array.from(documentRef.scripts).map(script => script.src.match(ASSET_PATTERN)?.[1]).find(Boolean)
  let lastCheck = -Infinity
  let pendingCheck = null
  let newest = null
  let navigation = 0

  async function check(force = false) {
    // 开发服务器没有哈希入口，由 webpack 的热更新和 liveReload 管理。
    if (!version) return null
    if (pendingCheck) return pendingCheck
    if (!force && now() - lastCheck < 15000) return newest
    lastCheck = now()
    pendingCheck = (async () => {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 2500)
      try {
        const response = await fetchImpl(`${origin}/index.html?__mq_check=${now()}`, { cache: 'no-store', signal: controller.signal })
        if (!response.ok) return null
        const release = parseRelease(await response.text(), origin)
        if (!release) return null
        if (release.version !== version) {
          const asset = await fetchImpl(release.asset, { method: 'HEAD', cache: 'no-store', signal: controller.signal })
          if (!asset.ok || !/javascript/i.test(asset.headers.get('content-type') || '')) return null
        }
        newest = release
        return release
      } catch {
        // 版本探测失败不阻塞业务导航，也不把断网误判为已发布新版本。
        return null
      } finally {
        clearTimeout(timeout)
        pendingCheck = null
      }
    })()
    return pendingCheck
  }

  function reload(path, targetVersion) {
    const target = new URL(path, origin)
    if (target.origin !== origin) return false
    const current = new URL(windowRef.location.href)
    if (current.searchParams.get(RELOAD_PARAM) === targetVersion) return false
    try {
      const last = JSON.parse(windowRef.sessionStorage.getItem(RELOAD_KEY) || 'null')
      if (last?.version === targetVersion && now() - last.time < 60000) return false
      windowRef.sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ version: targetVersion, time: now() }))
    } catch {
      // 禁用存储时仍由 URL 标记防止同一失败页面无限刷新。
    }
    target.searchParams.set(RELOAD_PARAM, targetVersion)
    windowRef.location.replace(target.href)
    return true
  }

  async function beforeNavigation(to) {
    const attempt = ++navigation
    const release = await check()
    if (attempt !== navigation) return false
    if (release && release.version !== version && reload(to.fullPath, release.version)) return false
    return true
  }

  async function onError(error, to) {
    if (!isChunkLoadError(error)) return
    const path = to?.fullPath || windowRef.location.href
    const release = await check(true)
    if (release && reload(path, release.version)) return
    notify({ message: '页面资源加载失败，请检查网络后重试。', href: new URL(path, origin).href, action: '重新加载' })
  }

  async function onResume() {
    if (documentRef.visibilityState !== 'visible') return
    const release = await check()
    if (release && release.version !== version) {
      // 停留在表单或监测页时仅提示；切换页面时再自动更新，避免中断正在编辑的内容。
      notify({ message: '页面已有新版本，切换页面时将自动更新。', href: windowRef.location.href, action: '立即更新' })
    }
  }

  function afterNavigation(to, from, failure) {
    if (failure) return
    const url = new URL(windowRef.location.href)
    if (!url.searchParams.has(RELOAD_PARAM)) return
    url.searchParams.delete(RELOAD_PARAM)
    windowRef.history.replaceState(windowRef.history.state, '', url.href)
  }

  return { beforeNavigation, onError, onResume, afterNavigation }
}

export function installAppUpdate(router) {
  const update = createAppUpdate({ notify: detail => window.dispatchEvent(new CustomEvent('microquake:page-update', { detail })) })
  router.beforeEach(update.beforeNavigation)
  router.onError(update.onError)
  router.afterEach(update.afterNavigation)
  document.addEventListener('visibilitychange', update.onResume)
  window.addEventListener('pageshow', update.onResume)
}
